// Binary "supports everything you picked" matching for the Add Integration picker. Every function
// takes picker entries shaped `{ integration, supportedTypes }` (supportedTypes = curated type
// names) plus the Set of selected curated type names, and never mutates its inputs.

// The most missing types a card names ("Missing: …"); cards missing more name nothing.
export const MAX_MISSING_TO_NAME = 2

// The most "Without <type>, K integrations do" suggestions offered when nothing fully matches.
export const MAX_DROP_ONE_SUGGESTIONS = 3

const byName = (a, b) => a.integration.name.localeCompare(b.integration.name)

// The selected types an integration does not support, alphabetically.
export const missingSelectedTypes = (supportedTypes, selectedTypes) => {
  const supported = new Set(supportedTypes)
  return [...selectedTypes].filter(name => !supported.has(name)).sort((a, b) => a.localeCompare(b))
}

// The missing types a card should name: only when it misses between 1 and MAX_MISSING_TO_NAME.
export const missingTypesToName = (supportedTypes, selectedTypes) => {
  const missing = missingSelectedTypes(supportedTypes, selectedTypes)
  return missing.length <= MAX_MISSING_TO_NAME ? missing : []
}

const supportsAll = (supportedTypes, selectedTypes) =>
  missingSelectedTypes(supportedTypes, selectedTypes).length === 0

// Splits entries into those supporting every selected type (alphabetical) and the rest (fewest
// missing first, then alphabetical). With nothing selected nothing counts as a full match, and
// every entry is returned alphabetically in `others`.
//
// A `generalist` entry (Visual Builder) covers any selection but is never one of the specific
// matches: when at least one specific integration fully matches it is listed last among
// `fullMatches`; when none does it is returned as an `alternatives` entry instead (so the picker
// can still say "no single integration covers all of these"). It is never in `others` with a
// selection active.
export const partitionByFullMatch = (entries, selectedTypes) => {
  if (selectedTypes.size === 0) return { fullMatches: [], alternatives: [], others: [...entries].sort(byName) }
  const generalists = entries.filter(item => item.generalist).sort(byName)
  const withMissing = entries.filter(item => !item.generalist)
    .map(item => ({ item, missing: missingSelectedTypes(item.supportedTypes, selectedTypes).length }))
  const specific = withMissing.filter(({ missing }) => missing === 0).map(({ item }) => item).sort(byName)
  const others = withMissing
    .filter(({ missing }) => missing > 0)
    .sort((a, b) => (a.missing - b.missing) || byName(a.item, b.item))
    .map(({ item }) => item)
  return specific.length > 0
    ? { fullMatches: [...specific, ...generalists], alternatives: [], others }
    : { fullMatches: [], alternatives: generalists, others }
}

// For each selected type, how many specific entries (Visual Builder, a generalist, is not counted)
// would fully match if that one type were dropped. Only types that unblock at least one entry are
// returned; best first, then alphabetical, capped.
export const dropOneSuggestions = (entries, selectedTypes) => {
  const selected = [...selectedTypes]
  const specific = entries.filter(item => !item.generalist)
  return selected
    .map(flowType => {
      const rest = selected.filter(name => name !== flowType)
      const count = specific.filter(item => supportsAll(item.supportedTypes, rest)).length
      return { flowType, count }
    })
    .filter(({ count }) => count > 0)
    .sort((a, b) => (b.count - a.count) || a.flowType.localeCompare(b.flowType))
    .slice(0, MAX_DROP_ONE_SUGGESTIONS)
}
